import {Link, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import type React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {fetchTenants, tenantsSelectors} from "../store/tenantsSlice";
import type {Plan} from "../store/plansSlice";
import {fetchPlans, plansSelectors} from "../store/plansSlice";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import type {TenantSummaryResponse} from "../types/tenant";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {getSubscriptionDisplayEndDate} from "../utils/subscriptionDates";
import {
    countTenantSegments,
    enabledFeatureCount,
    getTenantLifecycle,
    matchesSegment,
    type TenantLifecycle,
    type TenantSegment,
    type TenantStatus,
} from "../utils/tenantLifecycle";
import {planTone} from "../utils/planTone";
import {BOOLEAN_FEATURE_FLAGS} from "../constants/featureFlags";
import {PageHeader} from "../components/ui/PageHeader";
import {SegmentPills, type SegmentOption} from "../components/ui/SegmentPills";
import {MonoText, StatusChip, StatusDot} from "../components/ui/StatusChip";
import {calendarDaysUntil, formatDate} from "../util/format";
import {relativeDaysLabel} from "../util/relativeDays";
import {colors} from "../theme/tokens";

type SortColumn = "name" | "plan" | "subscriptionEnd" | "createdAt";
type SortDirection = "asc" | "desc";
type Density = "compact" | "roomy";

const FEATURE_BAR_CELLS = 8;

const STATUS_DOT_COLORS: Record<TenantStatus, string> = {
    paying: colors.success,
    free: colors.expiring,
    expired: colors.error,
    noSubscription: colors.placeholder,
};

const STATUS_LABEL_KEYS: Record<TenantStatus, string> = {
    paying: "TENANTS_STATUS_PAYING",
    free: "TENANTS_STATUS_FREE",
    expired: "TENANTS_SUBSCRIPTION_EXPIRED",
    noSubscription: "TENANTS_STATUS_NO_SUBSCRIPTION",
};

function getSubscriptionEnd(activeSubscription: TenantSummaryResponse["activeSubscription"]): Date | undefined {
    if (!activeSubscription) return undefined;
    return getSubscriptionDisplayEndDate(activeSubscription);
}

function getPlanName(planId: string | undefined, plansById: Map<string, Plan>): string {
    if (!planId) return "-";
    return plansById.get(planId)?.name ?? planId;
}

export function TenantsPage() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const status = useAppSelector((s) => s.tenants.status);
    const plansStatus = useAppSelector((s) => s.plans.status);
    const error = useAppSelector((s) => s.tenants.error);
    const tenants = useAppSelector(tenantsSelectors.selectAll);
    const plans = useAppSelector(plansSelectors.selectAll);
    const [search, setSearch] = useState("");
    const [segment, setSegment] = useState<TenantSegment>("all");
    const [density, setDensity] = useState<Density>("compact");
    const [sortColumn, setSortColumn] = useState<SortColumn>("name");
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
    const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
    const now = useMemo(() => new Date(), [tenants]);

    const plansById = useMemo(() => new Map(plans.map((p) => [p.id, p])), [plans]);
    const counts = useMemo(() => countTenantSegments(tenants, now), [tenants, now]);

    useEffect(() => {
        if (status !== "idle") return;
        void dispatch(fetchTenants());
    }, [dispatch, status]);

    useEffect(() => {
        if (plansStatus !== "idle") return;
        void dispatch(fetchPlans());
    }, [dispatch, plansStatus]);

    const visibleTenants = useMemo(() => {
        const term = search.trim().toLowerCase();
        const filtered = tenants.filter(
                (tenant) =>
                        matchesSegment(tenant, segment, now) &&
                        (term === "" || tenant.name.toLowerCase().includes(term) || tenant.id.toLowerCase().includes(term)),
        );

        const compare = (a: TenantSummaryResponse, b: TenantSummaryResponse): number => {
            switch (sortColumn) {
                case "name":
                    return a.name.localeCompare(b.name);
                case "plan":
                    return getPlanName(a.activeSubscription?.planId, plansById).localeCompare(
                            getPlanName(b.activeSubscription?.planId, plansById),
                    );
                case "subscriptionEnd":
                    return (getSubscriptionEnd(a.activeSubscription)?.getTime() ?? 0) - (getSubscriptionEnd(b.activeSubscription)?.getTime() ?? 0);
                case "createdAt":
                    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            }
        };
        const direction = sortDirection === "asc" ? 1 : -1;
        return [...filtered].sort((a, b) => direction * compare(a, b));
    }, [tenants, search, segment, sortColumn, sortDirection, plansById, now]);

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const focusRow = (index: number) => {
        const tenant = visibleTenants[Math.max(0, Math.min(index, visibleTenants.length - 1))];
        if (tenant) rowRefs.current.get(tenant.id)?.focus();
    };

    const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>, index: number, tenantId: string) => {
        if (event.key === "Enter") navigate(`/tenants/${tenantId}`);
        if (event.key === "j" || event.key === "ArrowDown") {
            event.preventDefault();
            focusRow(index + 1);
        }
        if (event.key === "k" || event.key === "ArrowUp") {
            event.preventDefault();
            focusRow(index - 1);
        }
    };

    const segmentOptions: SegmentOption<TenantSegment>[] = [
        {value: "all", label: t("TENANTS_SEGMENT_ALL"), count: counts.all, dotColor: colors.placeholder},
        {value: "paying", label: t("TENANTS_SEGMENT_PAYING"), count: counts.paying, dotColor: colors.success},
        {value: "free", label: t("TENANTS_SEGMENT_FREE"), count: counts.free, dotColor: colors.expiring},
        {value: "expiring", label: t("TENANTS_SEGMENT_EXPIRING"), count: counts.expiring, dotColor: colors.warning},
        {value: "expired", label: t("TENANTS_SEGMENT_EXPIRED"), count: counts.expired, dotColor: colors.error},
    ];

    const sortColumnLabels: Record<SortColumn, string> = {
        name: t("TENANTS_COLUMN_NAME"),
        plan: t("TENANTS_COLUMN_PLAN"),
        subscriptionEnd: t("TENANTS_COLUMN_SUBSCRIPTION_END"),
        createdAt: t("TENANTS_COLUMN_CREATED"),
    };

    const sortableHeader = (column: SortColumn, align?: "right") => (
            <TableCell align={align} sortDirection={sortColumn === column ? sortDirection : false}>
                <TableSortLabel
                        active={sortColumn === column}
                        direction={sortColumn === column ? sortDirection : "asc"}
                        onClick={() => handleSort(column)}
                >
                    {sortColumnLabels[column]}
                </TableSortLabel>
            </TableCell>
    );

    const rowPaddingY = density === "compact" ? "4px" : "10px";

    return (
            <>
                <PageHeader
                        title={t("NAV_TENANTS")}
                        subtitle={
                            <>
                                {t("TENANTS_SUMMARY_TOTAL", {count: counts.all})} · {t("TENANTS_SUMMARY_PAYING", {count: counts.paying})} ·{" "}
                                {t("TENANTS_SUMMARY_FREE", {count: counts.free})} ·{" "}
                                <Box component="span" sx={{color: counts.expired > 0 ? colors.error : undefined}}>
                                    {t("TENANTS_SUMMARY_EXPIRED", {count: counts.expired})}
                                </Box>
                            </>
                        }
                        actions={
                            <>
                                <DensityToggle value={density} onChange={setDensity} />
                                <Button component={Link} to="/tenants/new" variant="contained" startIcon={<AddIcon />}>
                                    {t("TENANTS_CREATE_BUTTON")}
                                </Button>
                            </>
                        }
                />

                {error && <Alert severity="error">{error}</Alert>}

                <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5}}>
                    <SegmentPills options={segmentOptions} value={segment} onChange={setSegment} aria-label={t("TENANTS_SEGMENTS_ARIA")} />
                    <Typography variant="body2" sx={{color: colors.faint}}>
                        {t("TENANTS_SORTED_BY", {column: sortColumnLabels[sortColumn]})} {sortDirection === "asc" ? "↑" : "↓"}
                    </Typography>
                    <Box sx={{flex: 1}} />
                    <TextField
                            placeholder={t("TENANTS_SEARCH_PLACEHOLDER")}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="small"
                            sx={{width: 280}}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{fontSize: 18, color: colors.faint}} />
                                            </InputAdornment>
                                    ),
                                },
                                htmlInput: {"aria-label": t("TENANTS_SEARCH_PLACEHOLDER")},
                            }}
                    />
                </Box>

                <Paper variant="outlined" sx={{position: "relative", overflow: "hidden"}}>
                    {status === "loading" && (
                            <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        bgcolor: "rgba(255,255,255,0.8)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        zIndex: 10,
                                    }}
                            >
                                <CircularProgress />
                            </Box>
                    )}
                    <TableContainer>
                        <Table aria-label={t("TENANTS_TABLE_ARIA")} sx={{"& .MuiTableBody-root .MuiTableCell-root": {py: rowPaddingY}}}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{width: 28, pr: 0}}>
                                        <Box component="span" sx={visuallyHidden}>{t("TENANTS_COLUMN_STATUS")}</Box>
                                    </TableCell>
                                    {sortableHeader("name")}
                                    {sortableHeader("plan")}
                                    <TableCell align="right">{t("TENANTS_COLUMN_SEATS")}</TableCell>
                                    {sortableHeader("subscriptionEnd")}
                                    <TableCell>{t("TENANTS_COLUMN_FEATURES")}</TableCell>
                                    {sortableHeader("createdAt", "right")}
                                    <TableCell sx={{width: 32}} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleTenants.map((tenant, index) => {
                                    const lifecycle = getTenantLifecycle(tenant, now);
                                    const subscription = tenant.activeSubscription;
                                    return (
                                            <TableRow
                                                    key={tenant.id}
                                                    hover
                                                    tabIndex={0}
                                                    ref={(element) => {
                                                        if (element) rowRefs.current.set(tenant.id, element);
                                                        else rowRefs.current.delete(tenant.id);
                                                    }}
                                                    sx={{cursor: "pointer", "&:focus-visible": {outline: `2px solid ${colors.primary}`, outlineOffset: -2}}}
                                                    onClick={() => navigate(`/tenants/${tenant.id}`)}
                                                    onKeyDown={(event) => handleRowKeyDown(event, index, tenant.id)}
                                            >
                                                <TableCell sx={{pr: 0}}>
                                                    <StatusDot color={STATUS_DOT_COLORS[lifecycle.status]} label={t(STATUS_LABEL_KEYS[lifecycle.status])} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{fontWeight: 500, color: colors.ink}}>{tenant.name}</Box>
                                                    <MonoText muted>{tenant.id}</MonoText>
                                                </TableCell>
                                                <TableCell>
                                                    {subscription ? (
                                                            <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                                                <StatusChip tone={planTone(subscription.planId, plans)}>
                                                                    {getPlanName(subscription.planId, plansById)}
                                                                </StatusChip>
                                                                {subscription.amountDueInCents <= 0 && (
                                                                        <Typography component="span" variant="caption" sx={{color: colors.faint}}>
                                                                            {t("TENANTS_PLAN_FREE")}
                                                                        </Typography>
                                                                )}
                                                            </Box>
                                                    ) : (
                                                            "-"
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">{subscription?.maxEmployees ?? "-"}</TableCell>
                                                <TableCell>
                                                    <SubscriptionEnd tenant={tenant} lifecycle={lifecycle} now={now} />
                                                </TableCell>
                                                <TableCell>
                                                    <FeatureBar enabled={enabledFeatureCount(tenant.featureConfig)} />
                                                </TableCell>
                                                <TableCell align="right" sx={{color: colors.body}}>
                                                    {formatDate(tenant.createdAt)}
                                                </TableCell>
                                                <TableCell sx={{color: colors.faint, pl: 0}}>
                                                    <ChevronRightIcon sx={{fontSize: 18, display: "block"}} />
                                                </TableCell>
                                            </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {status !== "loading" && visibleTenants.length === 0 && (
                            <Typography sx={{p: 3, textAlign: "center", color: colors.muted}}>
                                {tenants.length === 0 ? t("TENANTS_EMPTY_NONE") : t("TENANTS_EMPTY_FILTERED")}
                            </Typography>
                    )}
                    <Box sx={{px: 2, py: 1.25, borderTop: `1px solid ${colors.sectionDivider}`, fontSize: "12.5px", color: colors.muted}}>
                        {t("TENANTS_SHOWING", {shown: visibleTenants.length, total: tenants.length})}
                    </Box>
                </Paper>

                <Box aria-hidden sx={{display: {xs: "none", md: "flex"}, gap: 2, fontSize: "11.5px", color: colors.faint}}>
                    <span><KeyCap>j</KeyCap> / <KeyCap>k</KeyCap> {t("TENANTS_KEYS_MOVE")}</span>
                    <span><KeyCap>↵</KeyCap> {t("TENANTS_KEYS_OPEN")}</span>
                </Box>
            </>
    );
}

const visuallyHidden = {
    position: "absolute",
    width: 1,
    height: 1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
} as const;

function SubscriptionEnd({tenant, lifecycle, now}: {tenant: TenantSummaryResponse; lifecycle: TenantLifecycle; now: Date}) {
    const {t} = useTranslation();
    const subscription = tenant.activeSubscription;
    if (!subscription) return <>-</>;
    const displayedEnd = subscription.paidUntil ?? subscription.activeUntil;
    const hintColor = lifecycle.status === "expired" ? colors.error : lifecycle.expiringSoon ? colors.expiring : colors.muted;
    const accessDiffers = subscription.activeUntil !== displayedEnd;
    return (
            <Tooltip title={accessDiffers ? t("TENANTS_ACCESS_UNTIL", {date: formatDate(subscription.activeUntil)}) : ""} disableInteractive>
                <Box>
                    <Box sx={{color: colors.ink}}>{formatDate(displayedEnd)}</Box>
                    <Box sx={{fontSize: "11.5px", color: hintColor}}>{relativeDaysLabel(t, calendarDaysUntil(displayedEnd, now))}</Box>
                </Box>
            </Tooltip>
    );
}

function FeatureBar({enabled}: {enabled: number}) {
    const {t} = useTranslation();
    const total = BOOLEAN_FEATURE_FLAGS.length;
    const filledCells = Math.round((enabled / total) * FEATURE_BAR_CELLS);
    return (
            <Box
                    role="img"
                    aria-label={t("TENANTS_FEATURES_ON", {count: enabled, total})}
                    title={t("TENANTS_FEATURES_ON", {count: enabled, total})}
                    sx={{display: "flex", alignItems: "center", gap: 0.75}}
            >
                <Box sx={{display: "flex", gap: "2px"}}>
                    {Array.from({length: FEATURE_BAR_CELLS}, (_, index) => (
                            <Box
                                    key={index}
                                    sx={{width: 7, height: 12, borderRadius: "1px", backgroundColor: index < filledCells ? colors.primary : colors.cardBorder}}
                            />
                    ))}
                </Box>
                <MonoText muted>
                    {enabled}/{total}
                </MonoText>
            </Box>
    );
}

function DensityToggle({value, onChange}: {value: Density; onChange: (density: Density) => void}) {
    const {t} = useTranslation();
    const options: {value: Density; label: string}[] = [
        {value: "compact", label: t("TENANTS_DENSITY_COMPACT")},
        {value: "roomy", label: t("TENANTS_DENSITY_ROOMY")},
    ];
    return (
            <Box role="group" aria-label={t("TENANTS_DENSITY_ARIA")} sx={{display: "inline-flex", border: `1px solid ${colors.inputBorder}`, borderRadius: "4px", overflow: "hidden", height: 32}}>
                {options.map((option, index) => (
                        <ButtonBase
                                key={option.value}
                                onClick={() => onChange(option.value)}
                                aria-pressed={value === option.value}
                                sx={{
                                    px: 1.5,
                                    fontSize: "12.5px",
                                    borderLeft: index > 0 ? `1px solid ${colors.inputBorder}` : "none",
                                    backgroundColor: value === option.value ? colors.navHover : "#fff",
                                    color: value === option.value ? colors.ink : colors.muted,
                                    fontWeight: value === option.value ? 500 : 400,
                                }}
                        >
                            {option.label}
                        </ButtonBase>
                ))}
            </Box>
    );
}

function KeyCap({children}: {children: React.ReactNode}) {
    return (
            <Box component="kbd" sx={{fontFamily: "inherit", fontSize: "11px", fontWeight: 600, px: 0.5, borderRadius: "3px", backgroundColor: colors.tableHeader, color: colors.body}}>
                {children}
            </Box>
    );
}
